"use client";

import { Badge } from "@workspace/frontend-ui";
import type { FC } from "react";
import type { SelectedProvider } from "../../../types";
import EnabledProviderBadge from "./EnabledProviderBadge";
import useOverflowBadges from "./hooks/useOverflowBadges";

type Props = {
  enabledProviders: SelectedProvider[];
  onRemoveProvider: (provider: SelectedProvider) => void;
};

const EnabledProvidersBadges: FC<Props> = ({
  enabledProviders,
  onRemoveProvider,
}) => {
  const { containerRef, measureRefs, overflowRef, visibleCount } =
    useOverflowBadges(enabledProviders.length);
  const hiddenCount = enabledProviders.length - visibleCount;

  return (
    <div
      ref={containerRef}
      className="relative h-full flex-1 min-w-0 overflow-hidden flex items-center"
    >
      <div
        className="invisible absolute flex gap-1 pointer-events-none"
        aria-hidden
      >
        {enabledProviders.map((provider, index) => (
          <div
            key={provider.name}
            ref={(el) => {
              measureRefs.current[index] = el;
            }}
          >
            <EnabledProviderBadge
              provider={provider}
              onRemove={onRemoveProvider}
            />
          </div>
        ))}
        <div ref={overflowRef}>
          <Badge variant="secondary" className="px-1">
            +{enabledProviders.length}
          </Badge>
        </div>
      </div>
      <div className="flex gap-1">
        {enabledProviders.slice(0, visibleCount).map((provider) => (
          <EnabledProviderBadge
            key={provider.name}
            provider={provider}
            onRemove={onRemoveProvider}
          />
        ))}
        {hiddenCount > 0 && (
          <Badge variant="secondary" className="px-1">
            +{hiddenCount}
          </Badge>
        )}
      </div>
    </div>
  );
};

export default EnabledProvidersBadges;
